// City Hall → the CITY OVERVIEW (Ben's Oct 8 concept): one city at a glance, like Portal and Empire but for a single city.
// BUILDINGS lists every building with its next level's cost and a + that queues it (Ben, Oct 13 2026); construction mode
// on the city view (Construction.tsx) is the other way to build. Hero: City Hall level and build speed, the city's tags (CityTags.tsx), points / loyalty / build speed, the
// gear for the city's name. Then collapsible sections, closed until opened and remembered (state/collapse.ts):
// QUEUES · MOVEMENTS · RESOURCES · FARM SPACE | MARKET · TROOPS · ACADEMY | DEFENSE · BUILDINGS.
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import type { SupportItem } from "@/api/types";
import { art } from "@/kit/art";
import { CostLine, Countdown, ScreenHero, UnitMedal } from "@/kit/city";
import { IconChevronRight, IconClose, IconPlus, IconShield, IconSwords } from "@/kit/icons";
import { Confirm, Sheet } from "@/kit/overlays";
import { Bar, Button, Card, CardHead, Disc, Empty, Field, Medallion, Row, RowText, cx } from "@/kit/primitives";
import { useNow } from "@/state/clock";
import { useRememberedOpen } from "@/state/collapse";
import { BUILDING_KEYS, BUILDING_NAMES, RESOURCE_NAMES, TROOP_NAMES, type BuildingKey, type TroopKey } from "@/game/constants";
import { abbrev, fmtNum, plural } from "@/game/format";
import { fmtClock, fmtDuration, fmtSpan, fmtWhen } from "@/game/time";
import { WORLD, chSpeedup } from "@/game/rules";
import { buildBlock, buildingCost, buildingMap, constructionQueue, isMaxed, levelOf, merchantMovements, nextLevel, queueSlots, shortfall, movements, type MoveEntry, type QueueEntry } from "@/game/city";
import { attackAtHome, cityDefense, farmSplit, overviewQueues, resourceRows, troopRows, troopWords, type OverviewQueue } from "@/game/overview";
import { CityPage, useCity } from "./CityRoute";
import { CityTagRow, CityTagsSheet } from "./CityTags";
import { SpeedupSheet } from "./SpeedupSheet";
import { buildLink, screenFor } from "./CityView";

export function CityHall() {
  const { v, wid, vid, base } = useCity();
  const nav = useNavigate();
  const [rename, setRename] = useState(false);
  const [tags, setTags] = useState(false);
  if (!v) return <CityPage>{null}</CityPage>;
  const hq = levelOf(v, "b_hq");
  const speed = Math.round(chSpeedup(hq) * 100);
  return (
    <CityPage>
      <div className="stack ov">
        {/* Classic Frame (Oct 10 2026): no hero — the title bar, then a slim strip with what the hero said */}
        <ScreenHero
          title="City Overview"
          onBack={() => nav(base)}
          onGear={() => setRename(true)}
          gearLabel="City settings"
          className="ov-hero"
          stats={[
            { label: "Points", value: fmtNum(v.village?.points) },
            { label: "Loyalty", value: v.loyalty, small: "of 100", tone: v.loyalty < 100 ? "crim" : undefined },
            { label: "Build speed", value: `${speed}%`, small: "faster" },
          ]}
        >
          <p>City Hall <b className="gold">Lv. {hq}</b> · every build <b className="gold">{speed}%</b> faster</p>
          <CityTagRow wid={wid} vid={vid} onEdit={() => setTags(true)} />
        </ScreenHero>

        <QueuesSection />
        <MovementsSection />
        <ResourcesSection />
        <div className="ov-pair">
          <FarmSection />
          <MarketSection />
        </div>
        <TroopsSection />
        <div className="ov-pair">
          <AcademySection />
          <DefenseSection />
        </div>
        <BuildingsSection />
      </div>
      <RenameSheet open={rename} onClose={() => setRename(false)} />
      <CityTagsSheet open={tags} onClose={() => setTags(false)} wid={wid} vid={vid} cityName={v.village?.city_name ?? "This city"} />
    </CityPage>
  );
}

/** A collapsible card, closed until opened, remembered across sessions ("hall.<id>"). */
function Section({ id, title, aside, foot, className, children }: { id: string; title: ReactNode; aside?: ReactNode; foot?: ReactNode; className?: string; children: ReactNode }) {
  const [open, toggle] = useRememberedOpen(`hall.${id}`);
  return (
    <Card small={!open} className={cx("ov-card", className)} data-section={id}>
      <CardHead title={title} aside={aside} onToggle={toggle} open={open} />
      {open && children}
      {open && foot && <div className="card-foot">{foot}</div>}
    </Card>
  );
}

const More = ({ onClick, children }: { onClick: () => void; children: ReactNode }) => (
  <button type="button" className="link" onClick={onClick}>{children} <IconChevronRight style={{ width: "1em", height: "1em" }} /></button>
);

// ------------------------------------------------------------------ queues

function QueuesSection() {
  const { v, base } = useCity();
  const nav = useNavigate();
  const now = useNow();
  const [listOpen, toggleList] = useRememberedOpen("hall.queues.build");
  const rows = useMemo(() => (v ? overviewQueues(v, now) : []), [v, now]);
  const queue = useMemo(() => (v ? constructionQueue(v) : []), [v]);
  if (!v) return null;
  const busy = rows.filter((r) => r.busy).length;
  const end = queue.length ? queue[queue.length - 1].endMs : 0;
  const go = (r: OverviewQueue) =>
    nav(r.key === "build" ? buildLink(base) : r.key === "b_s" ? `${base}/forge` : r.key === "b_a" ? `${base}/academy` : `${base}/recruit/${r.key}`);
  return (
    <Section
      id="queues"
      title="Queues"
      aside={`${busy} of ${rows.length} busy`}
      foot={<>
        <span>{queue.length ? <>Build queue clear at <b className="gold">{fmtWhen(end, now)}</b></> : "Build queue empty"}</span>
        <More onClick={() => nav(buildLink(base, undefined, true))}>Construction</More>
      </>}
    >
      <div className="rows">
        {rows.map((r) => (
          <div key={r.key} className="stack" style={{ gap: 0 }}>
            <Row
              as="button"
              className={cx("ov-q", !r.busy && "idle", r.key === "build" && listOpen && "expanded")}
              onClick={() => (r.key === "build" && r.busy ? toggleList() : go(r))}
              aria-expanded={r.key === "build" && r.busy ? listOpen : undefined}
            >
              <Medallion src={art.med(r.key === "build" ? "b_hq" : r.key)} alt="" size={58} />
              <RowText
                eyebrow={<>{r.label}{r.count && <span className="cnt">{r.count}</span>}</>}
                line={<>{r.line}{r.more && <> · <b className="gold">{r.more}</b></>}</>}
              />
              <div className="right">
                {r.busy && r.endMs ? (
                  <div className="ov-q-time">
                    <span className="t"><Countdown endMs={r.endMs} /></span>
                    <Bar value={r.progress ?? 0} diamond />
                  </div>
                ) : (
                  <span className="btn steel sm ov-goto" aria-hidden="true">Go to</span>
                )}
              </div>
            </Row>
            {r.key === "build" && r.busy && listOpen && <BuildJobs queue={queue} />}
          </div>
        ))}
      </div>
    </Section>
  );
}

/** The build queue spelled out under the BUILD row: speed up the job building now, cancel any. */
function BuildJobs({ queue }: { queue: QueueEntry[] }) {
  const { wid, vid, actions } = useCity();
  const now = useNow();
  const [speedup, setSpeedup] = useState<QueueEntry | null>(null);
  const [cancel, setCancel] = useState<QueueEntry | null>(null);
  return (
    <div className="indent ov-jobs">
      {queue.map((q, i) => (
        <div key={`${q.item.key}-${q.item.level}-${i}`} className="kv">
          <span className="dia" />
          <span className="v">
            <b>{BUILDING_NAMES[q.item.key] ?? q.item.name}</b> {q.item.is_upgrade ? "to" : <span className="crimtxt">down to</span>} Lv. {q.item.level}
            <span className="greytxt"> · {i === 0 ? <Countdown endMs={q.endMs} /> : fmtDuration(q.durationMs)} · done {fmtWhen(q.endMs, now)}</span>
          </span>
          {i === 0 && <Button size="sm" kind="gold" onClick={() => setSpeedup(q)} disabled={!!q.item.has_used_speedup}>Speed up</Button>}
          <Disc size="xs" label={`Cancel ${BUILDING_NAMES[q.item.key] ?? q.item.name} level ${q.item.level}`} onClick={() => setCancel(q)}><IconClose /></Disc>
        </div>
      ))}
      <SpeedupSheet job={speedup} onClose={() => setSpeedup(null)} />
      <Confirm
        open={!!cancel}
        onClose={() => setCancel(null)}
        title="Cancel this job?"
        text={cancel ? `${BUILDING_NAMES[cancel.item.key] ?? cancel.item.name} level ${cancel.item.level} comes out of the queue and its resources come back. Jobs after it move up.` : ""}
        action="Cancel the job"
        busy={actions.isBusy("cancel_expand")}
        onConfirm={async () => {
          if (!cancel) return;
          const r = await actions.run("cancel_expand", () => api.village.cancelExpand({ wid, vid }, cancel.index));
          if (r) setCancel(null);
        }}
      />
    </div>
  );
}

// ------------------------------------------------------------------ movements

const MOVE_LABEL: Record<string, string> = {
  "in-attack": "Incoming attack", "in-support": "Support arriving", "out-attack": "Attack", "out-support": "Support",
  "in-return-attack": "Returning", "out-return-attack": "Returning", "in-return-support": "Support returning", "out-return-support": "Support returning",
};

function MovementsSection() {
  const { v, base } = useCity();
  const nav = useNavigate();
  const mv = useMemo(() => (v ? movements(v) : { incoming: [], outgoing: [] }), [v]);
  if (!v) return null;
  const moving = [...mv.incoming, ...mv.outgoing].sort((a, b) => (a.kind === "attack" && a.dir === "in" ? -1 : 0) - (b.kind === "attack" && b.dir === "in" ? -1 : 0) || a.endMs - b.endMs);
  const here = v.support?.received ?? [];
  const abroad = v.support?.sent ?? [];
  const total = moving.length + here.length + abroad.length;
  return (
    <Section id="movements" title="Movements" aside={total ? `${total} active` : "all quiet"} foot={<><span /><More onClick={() => nav(`${base}/rally/movement`)}>Rally Point</More></>}>
      <div className="rows">
        {moving.map((m) => <MoveRow key={`${m.dir}-${m.m.id}`} m={m} onOpen={() => nav(`${base}/rally/movement`)} onDefend={() => nav(`${base}/rally/movement`)} />)}
        {here.map((s) => <SupportRow key={`h-${s.id}`} s={s} here onOpen={() => nav(`${base}/rally/troops`)} />)}
        {abroad.map((s) => <SupportRow key={`a-${s.id}`} s={s} onOpen={() => nav(`${base}/rally/troops`)} />)}
        {!total && <Empty>No armies on the move and no support stationed.</Empty>}
      </div>
    </Section>
  );
}

function MoveRow({ m, onOpen, onDefend }: { m: MoveEntry; onOpen: () => void; onDefend: () => void }) {
  const threat = m.dir === "in" && m.kind === "attack";
  const label = MOVE_LABEL[`${m.dir}-${m.kind}`] ?? "Moving";
  const returning = m.kind.startsWith("return");
  const what = m.units ? troopWords(m.units) : threat ? "An army" : "Troops";
  const line = threat
    ? <>From <b className="gold">{m.other.name}</b> · lands <b className="creamt">{fmtClock(m.endMs)}</b> · in <Countdown endMs={m.endMs} /></>
    : returning
      ? <>{what} from <b className="gold">{m.other.name}</b></>
      : m.dir === "in"
        ? <>{what} from <b className="gold">{m.other.name}</b></>
        : <>{what} to <b className="gold">{m.other.name}</b></>;
  return (
    <Row alert={threat} className={cx("ov-mv", threat && "threat")}>
      <button type="button" className="rowhit" onClick={onOpen} aria-label={`${label} — open the Rally Point`}>
        <Medallion size={58} glow={threat ? "crim" : undefined}>{m.kind === "attack" || m.kind === "return-attack" ? <IconSwords /> : <IconShield />}</Medallion>
        <RowText eyebrow={label} line={line} />
      </button>
      <div className="right">
        {threat ? <Button kind="crimson" size="sm" onClick={onDefend}>Defend</Button> : <div className="val ov-q-time"><span className="t"><Countdown endMs={m.endMs} /></span></div>}
      </div>
    </Row>
  );
}

function SupportRow({ s, here, onOpen }: { s: SupportItem; here?: boolean; onOpen: () => void }) {
  const where = here ? s.origin_village_data?.city_name : s.target_village_data?.city_name;
  return (
    <Row as="button" className="ov-mv" onClick={onOpen}>
      <Medallion size={58}><IconShield /></Medallion>
      <RowText eyebrow={here ? "Support here" : "Support abroad"} line={<>{troopWords(s.units)} {here ? "from" : "at"} <b className="gold">{where ?? "a city"}</b> · stationed</>} />
      <div className="right"><span className="link">{here ? "Send home" : "Recall"} <IconChevronRight style={{ width: "1em", height: "1em" }} /></span></div>
    </Row>
  );
}

// ------------------------------------------------------------------ resources

function ResourcesSection() {
  const { v } = useCity();
  if (!v) return null;
  const rows = resourceRows(v);
  const wh = levelOf(v, "b_wh");
  const hp = levelOf(v, "b_h");
  const hidden = v.building_details?.b_h?.current_capacity;
  const perHour = rows.reduce((a, r) => a + r.rate, 0);
  return (
    <Section
      id="resources"
      title="Resources"
      aside={`Warehouse Lv. ${wh} · ${fmtNum(v.resources_capacity)} each`}
      foot={<>
        <span>Total <b className="gold">+{fmtNum(perHour)}/h</b> · {abbrev(perHour * 24)} a day</span>
        <span>{hp ? <>Hiding Place Lv. {hp} hides {fmtNum(hidden ?? 0)} of each</> : "No Hiding Place"}</span>
      </>}
    >
      <div className="ov-res">
        {rows.map((r) => {
          const full = r.fullInMs <= 0;
          return (
            <div key={r.r} className="ov-res-row">
              <img src={art.hud[r.r === "r_w" ? "wood" : r.r === "r_c" ? "clay" : "iron"]} alt="" />
              <div className="mid">
                <div className="eyebrow">{RESOURCE_NAMES[r.r]} <em>{BUILDING_NAMES[r.building]} {r.level}</em></div>
                <div className="amt"><b>{fmtNum(r.amount)}</b><span> / {fmtNum(r.cap)}</span></div>
                <Bar value={r.cap ? r.amount / r.cap : 0} color={full ? "crim" : undefined} />
              </div>
              <div className="rt">
                <b className="greentxt">+{fmtNum(r.rate)}/h</b>
                <span className={cx(full && "crimtxt")}>{full ? "Full" : Number.isFinite(r.fullInMs) ? `full in ${fmtSpan(r.fullInMs)}` : "—"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// ------------------------------------------------------------------ farm and market

function FarmSection() {
  const { v, base } = useCity();
  const nav = useNavigate();
  if (!v) return null;
  const f = farmSplit(v);
  const pct = (n: number) => `${f.max ? Math.min(100, (n / f.max) * 100) : 0}%`;
  const parts: [string, number, string][] = [["Home troops", f.home, "home"], ["Away", f.away, "away"], ["Training", f.training, "train"], ["Buildings", f.buildings, "bld"], ["Free", f.free, "free"]];
  return (
    <Section id="farm" title="Farm space" aside={`Lv. ${levelOf(v, "b_f")}`} foot={<><span>Room for {fmtNum(Math.floor(f.free / 3))} Knights</span><More onClick={() => nav(`${base}/b/b_f`)}>Farm</More></>}>
      <div className="ov-big"><b>{fmtNum(f.used)}</b><span> / {fmtNum(f.max)}</span></div>
      <div className="ov-seg" aria-hidden="true">
        {parts.slice(0, 4).map(([, n, c]) => <i key={c} className={c} style={{ width: pct(n) }} />)}
      </div>
      <div className="ov-legend">
        {parts.map(([label, n, c]) => (
          <div key={c}><i className={c} /><span>{label}</span><b>{fmtNum(n)}</b></div>
        ))}
      </div>
    </Section>
  );
}

function MarketSection() {
  const { v, base } = useCity();
  const nav = useNavigate();
  const now = useNow();
  if (!v) return null;
  const lvl = levelOf(v, "b_m");
  const mi = v.market_info;
  const mm = merchantMovements(v);
  const inc = mm.filter((m) => m.dir === "in").sort((a, b) => a.endMs - b.endMs);
  const out = mm.filter((m) => m.dir === "out");
  const offers = mi?.your_offers ?? [];
  const busy = Math.max(0, (mi?.total_merchants ?? 0) - (mi?.available_merchants ?? 0));
  const res = (k: string) => (k === "r_w" ? "wood" : k === "r_c" ? "clay" : "iron");
  return (
    <Section id="market" title="Market" aside={lvl ? `Lv. ${lvl}` : "not built"} foot={<><span>Carries {fmtNum(mi?.max_transport_amount || WORLD.market.carry)} each</span><More onClick={() => nav(`${base}/market`)}>Market</More></>}>
      {lvl ? (
        <>
          <div className="ov-med">
            <Medallion src={art.market.cart} alt="" size={64} />
            <div className="mid">
              <div className="ov-big sm"><b>{fmtNum(mi?.available_merchants)}</b><span> of {fmtNum(mi?.total_merchants)} home</span></div>
              <Bar value={mi?.total_merchants ? (mi.available_merchants ?? 0) / mi.total_merchants : 0} diamond />
            </div>
          </div>
          <div className="ov-kv">
            <div><span>Incoming</span><b>{inc.length ? <>{inc.length} · next {fmtDuration(Math.max(0, inc[0].endMs - now))}</> : "—"}</b></div>
            <div><span>Outgoing</span><b>{out.length ? `${out.length} · ${plural(busy, "merchant")}` : "—"}</b></div>
            <div><span>Open offers</span><b>{offers.length ? `${offers.length} · ${res(offers[0].offer_type)} for ${res(offers[0].for_type)}` : "—"}</b></div>
          </div>
        </>
      ) : (
        <Empty>No Market in this city yet.</Empty>
      )}
    </Section>
  );
}

// ------------------------------------------------------------------ troops

function TroopsSection() {
  const { v, base } = useCity();
  const nav = useNavigate();
  if (!v) return null;
  const rows = troopRows(v);
  const sum = rows.reduce((a, r) => ({ home: a.home + r.home, away: a.away + r.away, training: a.training + r.training, total: a.total + r.total }), { home: 0, away: 0, training: 0, total: 0 });
  const dash = (n: number, plus = false) => (n ? `${plus ? "+" : ""}${fmtNum(n)}` : "—");
  return (
    <Section
      id="troops"
      title="Troops"
      aside={`${fmtNum(sum.home)} home · ${fmtNum(sum.away)} away`}
      foot={<>
        <span>Attack at home <b className="gold">{abbrev(attackAtHome(v))}</b> <span className="ov-pipkey"><i className="on" /> researched <i className="forge" /> in the Forge</span></span>
        <More onClick={() => nav(`${base}/rally/troops`)}>Rally Point</More>
      </>}
    >
      {rows.length ? (
        <div className="ov-troops" role="table" aria-label="Troops">
          <div className="hd" role="row"><span role="columnheader" /><span role="columnheader">Forge</span><span role="columnheader">Home</span><span role="columnheader">Away</span><span role="columnheader">Training</span><span role="columnheader">Total</span></div>
          {rows.map((r) => (
            <div key={r.k} className="tr" role="row">
              <span className="u" role="cell"><UnitMedal troop={r.k as TroopKey} tier={0} size={40} /> {TROOP_NAMES[r.k]}</span>
              <span className="pips" role="cell" aria-label={`Research level ${r.level}${r.researching ? ", next level in the Forge" : ""}`}>
                {[1, 2, 3].map((i) => <i key={i} className={cx(i <= r.level && "on", r.researching && i === r.level + 1 && "forge")} />)}
              </span>
              <span role="cell">{dash(r.home)}</span>
              <span role="cell">{dash(r.away)}</span>
              <span role="cell" className={cx(r.training > 0 && "gold")}>{dash(r.training, true)}</span>
              <span role="cell" className="creamt">{fmtNum(r.total)}</span>
            </div>
          ))}
          <div className="tr all" role="row">
            <span className="u" role="cell">All units</span><span role="cell" />
            <span role="cell">{fmtNum(sum.home)}</span><span role="cell">{dash(sum.away)}</span><span role="cell">{dash(sum.training, true)}</span><span role="cell">{fmtNum(sum.total)}</span>
          </div>
        </div>
      ) : (
        <Empty>No troops yet — recruit at the Barracks.</Empty>
      )}
    </Section>
  );
}

// ------------------------------------------------------------------ academy and defense

function AcademySection() {
  const { v, base } = useCity();
  const nav = useNavigate();
  const now = useNow();
  if (!v) return null;
  const lvl = levelOf(v, "b_a");
  const a = v.academy;
  const edu = (v.queue_troops?.b_a ?? [])[0];
  return (
    <Section id="academy" title="Academy" aside={lvl ? `Lv. ${lvl}` : "not built"} foot={<><span /><More onClick={() => nav(lvl ? `${base}/academy` : buildLink(base, "b_a"))}>{lvl ? "Academy" : "Build it"}</More></>}>
      {lvl && a ? (
        <>
          <div className="ov-med">
            <Medallion src={art.med("b_a")} alt="" size={64} />
            <div className="mid">
              <div className="ov-big sm"><b>{fmtNum(a.scholars_total)}</b><span> of {fmtNum(a.scholar_limit)} scholars</span></div>
              <Bar value={a.scholar_limit ? a.scholars_total / a.scholar_limit : 0} diamond />
            </div>
          </div>
          <div className="ov-kv">
            <div><span>Educating</span><b>{a.scholars_in_education ? <>{a.scholars_in_education}{edu ? <> · {fmtDuration(Math.max(0, (edu.end_time_ms ?? 0) - now))}</> : null}</> : "—"}</b></div>
            <div><span>Scholarships</span><b>{fmtNum(a.scholarships)}</b></div>
            <div><span>Next scholar</span><b>{a.num_missing_scholarships_for_next_scholar_limit ? `${fmtNum(a.num_missing_scholarships_for_next_scholar_limit)} more scholarships` : "ready"}</b></div>
          </div>
        </>
      ) : (
        <Empty>No Academy in this city. It needs City Hall 25.</Empty>
      )}
    </Section>
  );
}

function DefenseSection() {
  const { v, base } = useCity();
  const nav = useNavigate();
  if (!v) return null;
  const d = cityDefense(v);
  return (
    <Section id="defense" title="Defense" aside={`Wall Lv. ${d.wall}`} foot={<><span /><More onClick={() => nav(`${base}/b/b_wa`)}>Wall</More></>}>
      <div className="ov-med">
        <Medallion size={64}><IconShield /></Medallion>
        <div className="mid">
          <div className="ov-big"><b>{abbrev(d.infantry)}</b><span> infantry def</span></div>
          <div className="greytxt ov-note">home troops, with wall</div>
        </div>
      </div>
      <div className="ov-kv">
        <div><span>Mounted def</span><b>{abbrev(d.mounted)}</b></div>
        <div><span>Wall bonus</span><b className="greentxt">+{fmtNum(d.flat)} · +{Math.round(d.pct * 100)}%</b></div>
        <div><span>Support here</span><b>{d.supportLabel}</b></div>
      </div>
    </Section>
  );
}

// ------------------------------------------------------------------ buildings (read-only; build in construction mode)

/** Every building with its next level's cost and a + that adds that level straight to the construction queue (Ben,
 *  Oct 13 2026: build from City Hall, like the live game's headquarters list). The rest of the row opens the building. */
function BuildingsSection() {
  const { v, base, wid, vid, actions } = useCity();
  const nav = useNavigate();
  const now = useNow();
  if (!v) return null;
  const map = buildingMap(v);
  const queue = constructionQueue(v);
  const slots = queueSlots(v);
  const queueFull = queue.length >= slots;
  const slotOpensInMs = queue[0] ? queue[0].endMs - now : undefined;
  const list = BUILDING_KEYS.map((k) => map[k]).filter(Boolean);
  const built = list.filter((b) => b.level > 0).length;
  const maxed = list.filter((b) => b.level > 0 && isMaxed(b)).length;
  const next = queue[0];
  const queuedTo = (k: BuildingKey) => {
    const q = queue.filter((x) => x.item.key === k);
    return q.length ? q[q.length - 1].item.level : null;
  };
  return (
    <Section
      id="buildings"
      title="Buildings"
      aside={`${built} built · ${maxed} maxed${next ? ` · ${BUILDING_NAMES[next.item.key]} ${next.item.level} next` : ""}`}
      foot={<><span>Queue <b className="gold">{queue.length} of {slots}</b>{queue.length ? <> · clear at <b className="gold">{fmtWhen(queue[queue.length - 1].endMs, now)}</b></> : null}</span><More onClick={() => nav(buildLink(base))}>Construction</More></>}
    >
      <div className="rows">
        {list.map((b) => {
          const to = queuedTo(b.key);
          const name = BUILDING_NAMES[b.key];
          const top = isMaxed(b);
          const lvl = nextLevel(b);
          const cost = buildingCost(b);
          const s = shortfall(v, cost);
          const reason = buildBlock(v, b, { queueFull, slotOpensInMs });
          const key = `hall-expand:${b.key}`;
          const fresh = b.level === 0 && to === null;
          return (
            <Row key={b.key} className={cx("ov-bld", !top && !reason && "ready")}>
              <button type="button" className="rowhit" onClick={() => nav(b.level > 0 ? screenFor(base, b.key) : buildLink(base, b.key))} aria-label={`Open ${name}`}>
                <Medallion src={art.med(b.key)} alt="" size={48} dim={b.level === 0} />
                <RowText
                  line={<><b className="creamt ov-bname">{name}</b> {b.level > 0 ? <span className="gold">Lv. {b.level}</span> : <span className="muted"><i>Not built</i></span>}{to !== null && <span className="gold ov-bto"> → {to}</span>}</>}
                  meta={top ? <span className="greytxt">Final level reached</span>
                    : reason && !/short|population/.test(reason) ? <span className="greytxt ov-bwhy">{reason}</span>
                    : <CostLine cost={cost} pop={cost.p_u} time={cost.time} short={s.short} size="sm" className="ov-bcost" />}
                />
              </button>
              <div className="right">
                {top ? <span className="c-maxpill">MAX</span> : (
                  <Button
                    kind={reason ? "steel" : "gold"}
                    size="sm"
                    className="ov-badd"
                    disabled={!!reason}
                    busy={actions.isBusy(key)}
                    title={reason ?? undefined}
                    aria-label={reason ? `${name}: ${reason}` : fresh ? `Build ${name} level 1` : `Add ${name} level ${lvl} to the queue`}
                    onClick={() => void actions.run(key, () => api.village.expand({ wid, vid }, b.key), { success: `${name} level ${lvl} added to the queue.` })}
                  >
                    <IconPlus /> {fresh ? "Build" : `Lv. ${lvl}`}
                  </Button>
                )}
              </div>
            </Row>
          );
        })}
      </div>
    </Section>
  );
}

// ------------------------------------------------------------------ city settings (the gear)

function RenameSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { v, wid, vid, actions } = useCity();
  const [name, setName] = useState(v?.village?.city_name ?? "");
  return (
    <Sheet open={open} onClose={onClose} label="City settings" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>City settings</h3>}>
      <div className="stack">
        <Field label="City name" value={name} onChange={(e) => setName(e.target.value)} maxLength={30} />
        <Button
          kind="crimson"
          wide
          disabled={!name.trim() || name.trim() === v?.village?.city_name}
          busy={actions.isBusy("rename")}
          onClick={async () => {
            const r = await actions.run("rename", () => api.village.rename({ wid, vid }, name.trim()), { success: "City renamed." });
            if (r) onClose();
          }}
        >
          Rename city
        </Button>
        <div className="c-note">{plural(levelOf(v, "b_hq"), "level")} of City Hall · {v?.village ? `founded on ${v.village.coords.replace(",", ", ")}` : ""}</div>
      </div>
    </Sheet>
  );
}
